import { Role } from '@customTypes/types';
import { MantineProvider, Switch } from '@mantine/core';
import { Notifications } from '@mantine/notifications';
import { getProfiles } from '@shared/api/requests';
import { APP_CONST } from '@shared/constants';
import { useQueryClient } from '@tanstack/react-query';
import { createRouter, RouterProvider } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { IntlProvider } from 'react-intl';
import { PhotoProvider } from 'react-photo-view';

import { ErrorPage, Loading, NotFoundPage } from '@/pages';
import { routeTree } from '@/routeTree.gen';
import {
  ARTIST_THEME,
  CLUB_THEME,
  DEFAULT_THEME
} from '@/shared/constants/mantine-theme';
import { useLocaleContext } from '@/shared/contexts';
import { messages } from '@/shared/i18n/messages';
// eslint-disable-next-line import/order
import { RootLayout } from '@/shared/layouts';

import 'react-photo-view/dist/react-photo-view.css';
import { getPlatformInfo } from '@/shared/utils/platformDetection';

import { usePushNotifications } from './shared/hooks/usePushNotifications';
import { LOCALES } from './shared/i18n/locales';
import { useProfileStore } from './shared/store/profileStore';

const router = createRouter({
  routeTree,
  context: {
    auth: {
      isAuthenticated: false,
      profile: null,
      currentSessionId: null
    }
  },
  defaultErrorComponent: ErrorPage,
  defaultNotFoundComponent: NotFoundPage,
  defaultPendingComponent: Loading
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

export const App = () => {
  const queryClient = useQueryClient();
  const { locale } = useLocaleContext();
  const {
    profile,
    isAuthenticated,
    currentSessionId,
    profileDataIsLoading,
    updateProfile,
    updateprofileDataIsLoading,
    updateCurrentSessionId
  } = useProfileStore();

  // usePushNotifications();
  // Получаем функцию регистрации пушей из хука
  const { requestPermissionAndRegister } = usePushNotifications();

  // Состояние для платформы
  const [isApplePlatform, setIsApplePlatform] = useState(false);
  const [isMacOS, setIsMacOS] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);

  console.log('Apple Web Push platform detected:', isApplePlatform, isMacOS);

  useEffect(() => {
    setIsApplePlatform(getPlatformInfo().isApplePlatform);
    setIsMacOS(getPlatformInfo().isMacOS);
  }, []);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const authToken = localStorage.getItem(
          APP_CONST.LOCAL_STORAGE.AUTH_TOKEN
        );
        if (authToken) {
          const profileData = await queryClient.fetchQuery({
            queryKey: ['initialProfileData'],
            queryFn: () => getProfiles({})
          });
          updateProfile(profileData.data);
          updateCurrentSessionId(profileData.data.profileIds[0]);
        }
        return true;
      } catch (e) {
        updateProfile(null);
        updateCurrentSessionId(null);
        localStorage.removeItem(APP_CONST.LOCAL_STORAGE.AUTH_TOKEN);
        localStorage.removeItem(APP_CONST.LOCAL_STORAGE.REFRESH_TOKEN);
        console.error(e);
      } finally {
        updateprofileDataIsLoading(false);
      }
    };

    if (!profile) {
      checkAuth();
    }
  }, []);

  // // Автоматическая регистрация пушей для НЕ Apple платформ
  // useEffect(() => {
  //   if (!isApplePlatform && isAuthenticated) {
  //     requestPermissionAndRegister();
  //   }
  // }, [isApplePlatform, isAuthenticated]);

  const getTheme = () => {
    if (profile == null) {
      return DEFAULT_THEME;
    }
    return profile.role === Role.artist ? ARTIST_THEME : CLUB_THEME;
  };

  useEffect(() => {
    (async () => {
      if ('serviceWorker' in navigator && 'PushManager' in window) {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        setIsSubscribed(!!sub);
      }
    })();
  }, []);

  return (
    <PhotoProvider>
      <IntlProvider
        defaultLocale='en'
        locale={LOCALES[locale]}
        messages={messages[LOCALES[locale]]}
      >
        <MantineProvider withCssVariables theme={getTheme()}>
          <RootLayout>
            <Notifications
              autoClose={3000}
              containerWidth={320}
              limit={2}
              position='top-center'
            />

            <div
              style={{
                background: 'white',
                padding: '6px 12px',
                borderRadius: 8,
                boxShadow: '0 0 10px rgba(0,0,0,0.1)'
              }}
            >
              {/* <Switch */}
              {/*   color='blue' */}
              {/*   label='Allow Push Notes' */}
              {/*   offLabel='OFF' */}
              {/*   size='sm' */}
              {/*   onLabel='ON' */}
              {/*   onChange={(event) => { */}
              {/*     if (event.currentTarget.checked) { */}
              {/*       requestPermissionAndRegister(); */}
              {/*     } */}
              {/*   }} */}
              {/* /> */}
              <Switch
                checked={isSubscribed}
                color='blue'
                label='Allow Push Notes'
                offLabel='OFF'
                size='sm'
                onLabel='ON'
                onChange={async (event) => {
                  if (event.currentTarget.checked) {
                    await requestPermissionAndRegister();
                    setIsSubscribed(true);
                  } else {
                    // Если хочешь поддерживать отписку — добавь:
                    const reg = await navigator.serviceWorker.ready;
                    const sub = await reg.pushManager.getSubscription();
                    if (sub) await sub.unsubscribe();
                    setIsSubscribed(false);
                  }
                }}
              />
            </div>

            {!profileDataIsLoading ? (
              <div>
                <RouterProvider
                  router={router}
                  context={{
                    auth: {
                      isAuthenticated,
                      profile,
                      currentSessionId
                    }
                  }}
                />
              </div>
            ) : (
              <Loading />
            )}
          </RootLayout>
        </MantineProvider>
      </IntlProvider>
    </PhotoProvider>
  );
};
